import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";
import useSound from "use-sound";

import QuizLayout from "./QuizLayout";
import QuestionCard from "./QuestionCard";
import ConfirmacionSummary from "./ConfirmacionSummary";
import CelebrationBomb from "./CelebrationBomb";
import Loader from "../../UI/Loader";
import NoInternetScreen from "../../UI/NoInternetScreen";

import {
  getCurrentConfirmacionThunk,
  sendConfirmacionAnswerThunk,
  getMisTalentosThunk,
  getConfirmacionesCompletadasThunk,
  getConfirmacionStatusThunk,
  getConfirmacionSummaryThunk,
} from "../../../store/confirmacion/confirmacion.thunks";
import {
  toggleFrase,
  clearSelectedFrases,
} from "../../../store/confirmacion/confirmacion.slice";

import clickSfx from "./click.mp3";
import "./css/confirmacionQuiz.css";

const ConfirmacionQuiz = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    currentQuestion,
    selectedFrases,
    talentosOptimos,
    confirmaciones,
    summary,
    loading,
    summaryLoading,
    status,
  } = useSelector((state) => state.confirmacion);

  const [playClick] = useSound(clickSfx, { volume: 0.25 });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [hasError, setHasError] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [previousTalento, setPreviousTalento] = useState(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Cargar datos iniciales
  useEffect(() => {
    const loadData = async () => {
      try {
        await dispatch(getMisTalentosThunk()).unwrap();
        await dispatch(getConfirmacionesCompletadasThunk()).unwrap();
        const pregunta = await dispatch(getCurrentConfirmacionThunk()).unwrap();
        
        // Si todas las confirmaciones están completadas, cargar el resumen
        if (pregunta.todas_completadas) {
          setIsCompleted(true);
          await dispatch(getConfirmacionSummaryThunk()).unwrap();
        }
      } catch (error) {
        setHasError(true);
        toast.error("Las confirmaciones de talentos aún no están disponibles", {
          position: "top-center",
          autoClose: 5000,
        });
        // Redirigir a mis talentos después de un momento
        setTimeout(() => {
          navigate("/mis-talentos");
        }, 2000);
      } finally {
        setIsInitialLoading(false);
      }
    };

    loadData();
  }, [dispatch, navigate]);

  // Detectar cambio de talento para mostrar confeti
  useEffect(() => {
    if (currentQuestion?.talento) {
      if (previousTalento && previousTalento !== currentQuestion.talento) {
        setShowCelebration(true);
        const timer = setTimeout(() => {
          setShowCelebration(false);
        }, 3000);
        return () => clearTimeout(timer);
      }
      setPreviousTalento(currentQuestion.talento);
    }
  }, [currentQuestion?.talento, previousTalento]);

  // Detectar conexión a internet
  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);

    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);

    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  const handleToggleFrase = (fraseId) => {
    playClick();
    dispatch(toggleFrase(fraseId));
  };

  const handleSubmit = async () => {
    //if (selectedFrases.length === 0) {
    //  toast.warning("Debes seleccionar al menos una frase", {
    //    position: "top-center",
    //    autoClose: 3000,
    //  });
    //  return;
    //}

    setIsSubmitting(true);

    try {
      const result = await dispatch(
        sendConfirmacionAnswerThunk({ frase_ids: selectedFrases })
      );

      if (result.type.endsWith("/rejected")) {
        toast.error(result.payload || "Error al guardar la respuesta", {
          position: "top-center",
          autoClose: 5000,
        });
        setIsSubmitting(false);
        return;
      }

      // Limpiar selección
      dispatch(clearSelectedFrases());

      // Actualizar confirmaciones completadas
      await dispatch(getConfirmacionesCompletadasThunk());

      // Verificar estado
      const statusResult = await dispatch(getConfirmacionStatusThunk());

      if (statusResult.payload?.todas_completadas) {
        // Quiz completado, redirigir a mis talentos
        toast.success("¡Has completado todas las confirmaciones!", {
          position: "top-center",
          autoClose: 3000,
        });
        setTimeout(() => {
          navigate("/mis-talentos");
        }, 1500);
      } else {
        // Obtener siguiente pregunta
        await dispatch(getCurrentConfirmacionThunk());
      }
    } catch (error) {
      toast.error("Error al procesar la respuesta", {
        position: "top-center",
        autoClose: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOnline) {
    return <NoInternetScreen />;
  }

  if (hasError) {
    return (
      <QuizLayout>
        <div className="quiz-scope">
          <QuestionCard>
            <div className="confirmacion-error">
              <Icon icon="mdi:alert-circle" />
              <h3>Funcionalidad en desarrollo</h3>
              <p>Las confirmaciones de talentos estarán disponibles próximamente.</p>
              <p style={{ marginTop: "1rem", fontSize: "0.9rem", opacity: 0.7 }}>
                Serás redirigido a tus talentos...
              </p>
            </div>
          </QuestionCard>
        </div>
      </QuizLayout>
    );
  }

  // Si está completado o en loading inicial, usar layout sin animación
  if (isCompleted || (isInitialLoading && !currentQuestion) || currentQuestion?.todas_completadas) {
    return (
      <div className="dashboard-main-body">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
          <h6 className="fw-semibold mb-0">Confirmación de Talentos</h6>
          <ul className="d-flex align-items-center gap-2">
            <li className="fw-medium">
              <a href="/#/mis-talentos" className="d-flex align-items-center gap-1 hover-text-primary">
                <Icon icon="mdi:arrow-left" className="text-lg" />
                Mis Talentos
              </a>
            </li>
          </ul>
        </div>
        
        {(isInitialLoading || loading || summaryLoading) ? (
          <div className="card radius-12">
            <div className="card-body p-24">
              <Loader
                center
                size="lg"
                type="dots"
                delay={150}
                text="Cargando..."
              />
            </div>
          </div>
        ) : (
          <ConfirmacionSummary summary={summary} />
        )}
      </div>
    );
  }

  // Loading para quiz activo
  if (loading && !currentQuestion) {
    return (
      <QuizLayout>
        <div className="quiz-scope">
          <QuestionCard>
            <Loader
              overlay
              size="lg"
              type="dots"
              delay={150}
              text="Cargando confirmación..."
            />
          </QuestionCard>
        </div>
      </QuizLayout>
    );
  }

  if (!currentQuestion) {
    return (
      <QuizLayout>
        <div className="quiz-scope">
          <QuestionCard>
            <div className="confirmacion-error">
              <Icon icon="mdi:alert-circle" />
              <p>No hay preguntas de confirmación disponibles</p>
            </div>
          </QuestionCard>
        </div>
      </QuizLayout>
    );
  }

  const { pregunta, progreso, total_preguntas, talento } = currentQuestion;

  return (
    <QuizLayout>
      <div className="quiz-scope">
        <QuestionCard>
          {(loading || isSubmitting) && (
            <Loader
              overlay
              size="lg"
              type="dots"
              delay={150}
              text={isSubmitting ? "Guardando respuesta..." : "Cargando..."}
            />
          )}

          {/* Celebración al cambiar de talento */}
          {showCelebration && (
            <CelebrationBomb
              message={`¡Nuevo talento: ${currentQuestion.talento}!`}
              intensity={50}
              duration={2.5}
              size="md"
            />
          )}

          {/* Header con progreso */}
          <div className="confirmacion-header">
            <div className="confirmacion-progress">
              <Icon icon="mdi:progress-check" />
              <span className="confirmacion-progress-text">
                Progreso: {progreso} / {total_preguntas}
              </span>
            </div>
          </div>

          {/* Nombre del talento */}
          <div className="confirmacion-talento">
            <Icon icon="mdi:star-box" />
            <h2 className="confirmacion-talento-name">{talento}</h2>
          </div>

          {/* Pregunta */}
          <h3 className="confirmacion-question">{pregunta.pregunta}</h3>

          {/* Frases seleccionables */}
          <AnimatePresence mode="wait">
            <motion.div
              key={pregunta.id}
              className="confirmacion-frases"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {pregunta.frases.map((frase) => {
                const isSelected = selectedFrases.includes(frase.id);
                return (
                  <motion.div
                    key={frase.id}
                    className={`confirmacion-frase ${isSelected ? "selected" : ""}`}
                    onClick={() => handleToggleFrase(frase.id)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="confirmacion-frase-checkbox">
                      {isSelected ? (
                        <Icon icon="mdi:checkbox-marked" />
                      ) : (
                        <Icon icon="mdi:checkbox-blank-outline" />
                      )}
                    </div>
                    <p className="confirmacion-frase-text">{frase.frase}</p>
                  </motion.div>
                );
              })}
            </motion.div>
          </AnimatePresence>

          {/* Botón de guardar */}
          <button
            className="confirmacion-submit-btn"
            onClick={handleSubmit}
            disabled={isSubmitting || loading }
          >
            <Icon icon="mdi:content-save" />
            <span>Guardar y continuar</span>
          </button>
        </QuestionCard>
      </div>
    </QuizLayout>
  );
};

export default ConfirmacionQuiz;
